/* eslint-disable no-await-in-loop */
import React, { useEffect, useRef, useState } from "react";

import {
  IStyle,
  mergeStyles,
  MessageBar,
  MessageBarType,
  PrimaryButton
} from "@bps/fluent-ui";

import { useTrackAnalyticsEvent } from "../analytics/app-insights.hooks";
import { AnalyticsEvent } from "../analytics/app-insights.types";

export interface IDownloadWidget {
  buttonText: string;
  buttonStyles?: IStyle;
  isReadOnly?: boolean;
  getSurveyVariable?: (name: string) => any;
}

export const DownloadInfoWidget: React.FC<IDownloadWidget> = ({
  buttonText,
  buttonStyles,
  isReadOnly,
  getSurveyVariable
}) => {
  const [error, setError] = useState<string | undefined>();
  const [infoMessage, setInfoMessage] = useState<string | undefined>();
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadedUrlRef = useRef<string | undefined>();
  const buttonRootStyles: IStyle = mergeStyles({ width: 280 }, buttonStyles);
  const logEvent = useTrackAnalyticsEvent();

  useEffect(
    () => () => {
      if (downloadedUrlRef.current?.startsWith("blob:")) {
        URL.revokeObjectURL(downloadedUrlRef.current);
      }
    },
    []
  );

  if (!isReadOnly) {
    return null;
  }

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (isDownloading) {
      return;
    }

    if (downloadedUrlRef.current) {
      window.open(downloadedUrlRef.current, "_blank");
      return;
    }

    setError(undefined);

    const getDocumentDownloadData = getSurveyVariable?.(
      "_getDocumentDownloadData"
    ) as (() => Promise<any>) | undefined;

    if (!getDocumentDownloadData) {
      setError("Download is not available.");
      return;
    }

    setIsDownloading(true);
    setInfoMessage("Preparing your download...");
    try {
      const downloadOptions = await getDocumentDownloadData();

      const pollPeriodMs = 1000 * downloadOptions.pollPeriodSeconds;
      const timeOutPeriodMs = 1000 * downloadOptions.timeOutPeriodSeconds;
      const blobUrl = downloadOptions.blobUrl;

      // Poll until document is ready or timeout occurs

      // wait-time backoff function
      const getWaitMs = (attempt: number) =>
        attempt === 0 ? pollPeriodMs / 3 :
        attempt === 1 ? pollPeriodMs / 2 :
        pollPeriodMs;

      let attempt = 0;
      let lastError: string | null = null;
      const startTime = Date.now();  

      while (Date.now() - startTime < timeOutPeriodMs) {
        try {
          // wait for document generation
          await new Promise(resolve =>
            setTimeout(resolve, getWaitMs(attempt)));

          // don't exceed the timeout period
          if (Date.now() - startTime >= timeOutPeriodMs) {
            break;
          }

          const response = await fetch(blobUrl);

          // 404 = document not ready yet, continue polling
          if (response.status === 404) {
            ++attempt;
            continue;
          }

          // Success status = document ready, download blob
          if (response.ok) {
            setInfoMessage("Download is starting...");
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            downloadedUrlRef.current = objectUrl;
            window.open(objectUrl, "_blank");
            setTimeout(() => setInfoMessage(undefined), 3000);
            return;
          }

          // Other error status
          lastError = `Server returned status ${response.status}`;
          break;
        } catch (fetchError) {
          ++attempt;
          lastError =
            fetchError instanceof Error
              ? fetchError.message
              : "Network error during polling";
          logEvent(AnalyticsEvent.documentDownloadNetworkError, {
            errorMessage: lastError
          });
          break;
        }
      }

      // Timeout reached or error occurred
      setInfoMessage(undefined);
      setError(
        lastError ||
          "Document is not ready for download. Please try again later."
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Error downloading document";
      logEvent(AnalyticsEvent.documentDownloadError, { errorMessage });
      setInfoMessage(undefined);
      setError(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };
  return (
    <>
      <PrimaryButton
        primary
        onClick={handleClick}
        disabled={isDownloading}
        // eslint-disable-next-line react/forbid-component-props
        styles={{
          root: buttonRootStyles,
          rootDisabled: buttonRootStyles
        }}
        iconProps={{ iconName: "Download" }}
      >
        {buttonText}
      </PrimaryButton>
      {infoMessage && (
        <MessageBar
          messageBarType={MessageBarType.info}
          isMultiline={false}
          onDismiss={() => setInfoMessage(undefined)}
        >
          {infoMessage}
        </MessageBar>
      )}
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={() => setError(undefined)}
        >
          {error}
        </MessageBar>
      )}
    </>
  );
};
